import React, { Component } from 'react';
import * as R from 'ramda';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { toDateFormat } from 'core/helpers';
import dayjs from 'dayjs';
import DayPickerInput from 'react-day-picker/DayPickerInput';
import {
  formatDate,
  parseDate,
} from 'react-day-picker/moment';
import { Input } from 'components/form-elements/input/input';
import { isPast } from '../../../helpers/date';
import 'react-day-picker/lib/style.css';
import './date-picker-input.scss';

export class DatePickerInput extends Component {
  static propTypes = {
    onChange: PropTypes.func.isRequired,
    boxDateFormat: PropTypes.string,
    value: PropTypes.string,
    error: PropTypes.shape(),
    preventPastDates: PropTypes.bool,
    isRawDates: PropTypes.bool,
    validateToday: PropTypes.bool,
    todayValidTill: PropTypes.shape(),
    icon: PropTypes.shape(),
  };

  static defaultProps = {
    error: null,
    value: '',
    boxDateFormat: '',
    isRawDates: false,
    preventPastDates: false,
    validateToday: false,
    todayValidTill: false,
    icon: null,
  };

  state = {
    selectedDay: null,
  }

  componentDidMount() {
    const { selectedDay } = this.state;

    if (!selectedDay) {
      this.setDateFromCore();
    }
  }

  isTodayValid = () => {
    const { todayValidTill } = this.props;

    let dateToCompare = dayjs();

    if (!R.isNil(todayValidTill?.h)) {
      dateToCompare = dateToCompare.set('hours', todayValidTill.h || 24).set('minutes', 0).set('seconds', 0);
    }

    if (!R.isNil(todayValidTill?.m)) {
      dateToCompare = dateToCompare.set('minutes', todayValidTill.m || 0).set('seconds', 0);
    }

    return dayjs().isBefore(dateToCompare, 'hours'); // today is valid till given time
  }

  checkIsDateValid = (date) => {
    const { preventPastDates, validateToday } = this.props;

    if (!dayjs(date).isValid()) {
      return false;
    }

    const isSameDay = dayjs().isSame(date, 'date');
    let isDateValid = true;

    if (preventPastDates) {
      isDateValid = !isPast(date, 'date');
    }

    if (validateToday && isSameDay) {
      isDateValid = this.isTodayValid();
    }

    return isDateValid;
  }

  onDayChange = (day) => {
    const { onChange, boxDateFormat, isRawDates } = this.props;

    if (day) {
      this.setState({ selectedDay: day });
      onChange(isRawDates ? day : toDateFormat(day, boxDateFormat));
    }
  }

  handleDayClick = (day) => {
    const { onChange, boxDateFormat, isRawDates } = this.props;
    const isDateValid = this.checkIsDateValid(day);

    if (!isDateValid) {
      return;
    }

    this.setState({ selectedDay: day });
    onChange(isRawDates ? day : toDateFormat(day, boxDateFormat));
  }

  setDateFromCore = () => {
    const {
      value,
      boxDateFormat,
      onChange,
      isRawDates
    } = this.props;

    if (!this.checkIsDateValid(value)) {
      let validDate = dayjs();

      if (!this.isTodayValid()) {
        validDate = validDate.add(1, 'day');
      }

      this.setState({ selectedDay: validDate.toDate() });
      onChange(validDate.toDate());

      return;
    }

    this.setState(isRawDates ? { selectedDay: value }
      : { selectedDay: new Date(toDateFormat(value, 'YYYY-MM-DDT00:00:00', boxDateFormat)) });
  }

  render() {
    const {
      onChange, error, boxDateFormat, preventPastDates, icon, ...props
    } = this.props;
    const { selectedDay } = this.state;

    return (
      <DayPickerInput
        format={boxDateFormat}
        formatDate={formatDate}
        parseDate={parseDate}
        dayPickerProps={{
          selectedDays: this.state.selectedDay,
          onDayClick: this.handleDayClick,
          showOutsideDays: true,
          disabledDays: preventPastDates ? { before: new Date() } : null,
        }}
        onDayChange={this.onDayChange}
        inputProps={{
          className: classNames('DayPickerInput-input form-control w-100 input-inner', {
            'border-danger error': !!error,
          }),
          onBlur: this.setDateFromCore,
        }}
        // eslint-disable-next-line react/jsx-no-bind
        component={inputProps => <Input {...inputProps} icon={icon} />}
        {...props}
        value={selectedDay}
      />
    );
  }
}
